// src/pages/ConclusionPage.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common/Button';
import { getConclusion, updateConclusion, generateConclusion, getResultDetail, getCustomAnswers, saveCustomAnswer, deleteConclusion, saveConclusionPdf, downloadConclusionPdf, patchConclusionAccess } from '../api/results';
import type { ConclusionResponse, ResultDetail, CustomAnswerRow, QuestionAnswer, ConclusionVersion, ConclusionRequestDataResult } from '../api/types';


/* ── Screen layout ── */
const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.background};
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetaValue = styled.span`
  font-size: 0.92rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 300px;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  line-height: 1.8;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ErrorText = styled.p`
  font-size: 0.88rem;
  color: #e53e3e;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const TextActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const GeneratingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.9rem;
`;

const PulseArrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.3rem;
  animation: pulseArrow 1.2s ease-in-out infinite;

  @keyframes pulseArrow {
    0%, 100% { opacity: 0.35; transform: translateX(0); }
    50% { opacity: 1; transform: translateX(4px); }
  }
`;

/* ── Modal overlay ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1400px;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

/* ── Time bar chart ── */
interface ChartEntry {
  order: number;
  content: string;
  time: number;
  skipped: boolean;
}

const TimeBarChart: React.FC<{ entries: ChartEntry[]; noDataLabel?: string }> = ({ entries, noDataLabel }) => {
  if (!entries.length) return <p style={{ color: '#888', margin: '16px 0' }}>{noDataLabel || 'Нет данных о времени'}</p>;

  const times = entries.map(e => e.time);
  const maxTime = Math.max(...times, 1);
  const mean = times.reduce((a, b) => a + b, 0) / times.length;
  const sigma = Math.sqrt(times.reduce((a, t) => a + (t - mean) ** 2, 0) / times.length);

  const s2hi = mean + 2 * sigma;
  const s2lo = mean - 2 * sigma;
  const s3hi = mean + 3 * sigma;
  const s3lo = mean - 3 * sigma;

  const GAP = 3;
  const H = 360;
  const PAD_TOP = 24;
  const PAD_BOT = 36;
  const BAR_W = Math.min(48, Math.max(6, Math.floor(1360 / entries.length) - GAP));
  const showLabels = BAR_W >= 10;
  const totalW = entries.length * (BAR_W + GAP);
  const svgW = Math.max(totalW, 300);

  const timeToY = (t: number) => PAD_TOP + H - Math.round((Math.min(t, maxTime) / maxTime) * H);

  const hLine = (t: number, color: string, dash: string, label: string) => {
    if (t <= 0 || sigma === 0) return null;
    const y = timeToY(t);
    if (y < PAD_TOP - 2 || y > PAD_TOP + H) return null;
    return (
      <g key={label}>
        <line x1={0} y1={y} x2={svgW} y2={y} stroke={color} strokeWidth={1} strokeDasharray={dash} opacity={0.75} />
        <text x={svgW - 2} y={y - 3} fontSize={11} fill={color} textAnchor="end">{label}</text>
      </g>
    );
  };

  const out3 = sigma > 0 ? times.filter(t => t > s3hi || t < s3lo).length : 0;
  const out2 = sigma > 0 ? times.filter(t => (t > s2hi && t <= s3hi) || (t < s2lo && t >= s3lo)).length : 0;

  return (
    <div style={{ overflowX: 'auto', padding: '4px 0' }}>
      <svg width={svgW} height={H + PAD_TOP + PAD_BOT} style={{ display: 'block' }}>
        <text x={2} y={PAD_TOP - 6} fontSize={12} fill="#aaa">{maxTime} ms</text>

        {/* σ reference lines */}
        {hLine(s3hi, '#ef4444', '3,2', '+3σ')}
        {hLine(s2hi, '#f97316', '4,2', '+2σ')}
        {sigma > 0 && hLine(mean, '#94a3b8', '2,2', 'μ')}
        {hLine(s2lo, '#f97316', '4,2', '-2σ')}
        {hLine(s3lo, '#ef4444', '3,2', '-3σ')}

        {entries.map((e, i) => {
          const barH = Math.max(Math.round((e.time / maxTime) * H), 2);
          const x = i * (BAR_W + GAP);
          const y = PAD_TOP + H - barH;
          let fill = '#4a90e2';
          if (e.skipped) fill = '#f59e0b';
          else if (sigma > 0 && (e.time > s3hi || e.time < s3lo)) fill = '#ef4444';
          else if (sigma > 0 && (e.time > s2hi || e.time < s2lo)) fill = '#f97316';
          return (
            <g key={i}>
              <rect x={x} y={y} width={BAR_W} height={barH} fill={fill} rx={2} opacity={0.85}>
                <title>{`${e.order}. ${e.content.length > 50 ? e.content.slice(0, 50) + '…' : e.content}: ${e.time} ms`}</title>
              </rect>
              {showLabels && (
                <text x={x + BAR_W / 2} y={PAD_TOP + H + 18} fontSize={12} textAnchor="middle" fill="#888">
                  {e.order}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ marginTop: 8, fontSize: '0.9rem', color: '#888', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#4a90e2', borderRadius: 2, marginRight: 4 }} />норма</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f59e0b', borderRadius: 2, marginRight: 4 }} />пропуск</span>
        {sigma > 0 && <><span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f97316', borderRadius: 2, marginRight: 4 }} />за ±2σ{out2 > 0 ? ` (${out2})` : ''}</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#ef4444', borderRadius: 2, marginRight: 4 }} />за ±3σ{out3 > 0 ? ` (${out3})` : ''}</span></>}
        {sigma > 0 && <span style={{ color: '#94a3b8', marginLeft: 4 }}>μ = {Math.round(mean)} мс, σ = {Math.round(sigma)} мс</span>}
      </div>
    </div>
  );
};

/* ── Answers table (reused in modal) ── */
const ATable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;
const ATh = styled.th`
  text-align: left;
  padding: 7px 10px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.75rem;
  text-transform: uppercase;
`;
const ATd = styled.td`
  padding: 7px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  vertical-align: top;
`;

/* ── Participant switcher ── */
const ParticipantTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;
const ParticipantTab = styled.button<{ $active: boolean }>`
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.82rem;
  cursor: pointer;
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) => $active ? '#fff' : theme.colors.text};
  transition: all 0.15s;
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Confirm modal (separate from the custom-answers modal) ── */
const ConfirmModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

/* ── Conclusion page ── */

const formatElapsed = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const ConclusionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState<ConclusionResponse | null>(null);
  const [details, setDetails] = useState<(ResultDetail | null)[]>([]);
  const [activeDetailIdx, setActiveDetailIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editText, setEditText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [hasGeneratedText, setHasGeneratedText] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingElapsed, setGeneratingElapsed] = useState(0);
  // Флаг-«живой» поллинг: пока компонент смонтирован. pollGeneration сам
  // планирует следующий 5-секундный шаг; cleanup останавливает цепочку при
  // уходе со страницы и отменяет отложенный вызов.
  const pollingTimerRef = useRef<number | null>(null);
  const pollingActiveRef = useRef(true);
  const [generateError, setGenerateError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfDownloadError, setPdfDownloadError] = useState('');

  const [showChart, setShowChart] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customAnswers, setCustomAnswers] = useState<CustomAnswerRow[]>([]);
  const [customEdits, setCustomEdits] = useState<Record<string, { coefficient: string; parameters: Record<string, string> }>>({});
  const [customSaving, setCustomSaving] = useState<Record<string, boolean>>({});

  const [toastMsg, setToastMsg] = useState('');
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Режим поля: 'raw' — первичные данные из request_data; 'saved' — текст
  // выбранной версии заключения. По умолчанию при открытии показываем первичку,
  // чтобы её можно было сразу отправить в LLM (в т.ч. повторно).
  const [mode, setMode] = useState<'raw' | 'saved'>('raw');
  const [rawText, setRawText] = useState('');
  const [activeVersion, setActiveVersion] = useState(1);
  const [versionSaving, setVersionSaving] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');

  const isDirty = editText !== savedText;
  const versions: ConclusionVersion[] = data?.versions ?? [];
  const activeIsCurrent = activeVersion === (data?.iteration ?? 1);
  const isColorMode: boolean = (data?.request_data?.results || []).some((r) => r.color_mode);
  // Генерация уже идёт (запущена ранее/в другой вкладке) — показываем индикатор.
  const generationInProgress = (data?.generation_status === 'processing' || generating);

  // Текст/статус активной версии (для переключателя «Первичные данные/Заключение»).
  const activeVersionText = activeIsCurrent
    ? (data?.response_data?.text ?? data?.response_data?.result ?? '')
    : (versions.find(v => v.iteration === activeVersion)?.text ?? '');

  const pollGeneration = useCallback(async (): Promise<void> => {
    if (!id) return;
    // Последующий шаг цепочки: если компонент уже размонтирован — не продолжаем.
    if (!pollingActiveRef.current) return;
    try {
      const current = await getConclusion(Number(id));
      if (!pollingActiveRef.current) return;
      setData(current);
      const status = current.generation_status || 'processing';
      const txt: string = current.response_data?.text ?? current.response_data?.result ?? '';
      if (status === 'ready' || status === 'error' || (txt && status !== 'processing')) {
        setGenerating(false);
        setMode('saved');
        setActiveVersion(current.iteration ?? 1);
        setEditText(txt);
        setSavedText(txt);
        setHasGeneratedText(!!txt);
        if (txt) {
          try {
            const pdf = await saveConclusionPdf(Number(id));
            setData((cur: ConclusionResponse | null) => cur ? {
              ...cur,
              pdf_path: pdf.path || cur.pdf_path,
              pdf_status: pdf.status || cur.pdf_status,
              pdf_url: pdf.pdf_url || pdf.url || cur.pdf_url,
              pdf_ready: true,
            } : cur);
          } catch {
            // PDF не сформирован — ошибка не критична, видно по отсутствию кнопки.
          }
        }
        return;
      }
      if (!pollingActiveRef.current) return;
      pollingTimerRef.current = window.setTimeout(() => { void pollGeneration(); }, 5000);
    } catch (e: unknown) {
      if (!pollingActiveRef.current) return;
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setGenerateError(detail || t('conclusionGenerateError'));
      setGenerating(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getConclusion(Number(id))
      .then(async d => {
        setData(d);
        if ((d.generation_status || 'idle') === 'processing') {
          setGenerating(true);
          void pollGeneration();
        }
        const isColor: boolean = (d.request_data?.results || []).some(
          (r) => r.color_mode,
        );
        const txt: string = d.response_data?.text ?? d.response_data?.result ?? '';
        const algoText: string = (d.request_data?.results || [])
          .map((r) => r.algorithm_text || '')
          .filter(Boolean)
          .join('\n\n');
        // Первичные данные (алгоритмический текст + метаданные) всегда
        // показываются админу в режиме «Первичные данные», чтобы их можно
        // повторно отправить в LLM (альтернативное заключение). Для цветомера
        // используется TSV-таблица из request_data.
        const raw: string = isColor && buildColorRawData(d)
          ? buildColorRawData(d)
          : algoText;
        setRawText(raw);
        // Начальный режим: если есть сохранённая версия, открываем её (история
        // доступна селектором); иначе — первичные данные.
        const draft: string = d.admin_draft?.text ?? '';
        const displayText = draft || txt || raw;
        const hasSaved = !!txt;
        const initIter = d.iteration ?? 1;
        setActiveVersion(initIter);
        setMode(hasSaved ? 'saved' : 'raw');
        setEditText(hasSaved ? txt : raw);
        setSavedText(hasSaved ? txt : raw);
        setHasGeneratedText(hasSaved);

        const ids: number[] = d.result_ids || [];
        if (ids.length > 0) {
          // Загружаем детали (для диаграммы и просмотра ответов)
          const loaded = (await Promise.all(
            ids.map((rid: number) => getResultDetail(rid).catch(() => null)),
          )).filter(Boolean) as ResultDetail[];

          // Если первичек нет (удалены) — строим псевдо-детали из сохранённого
          // request_data, чтобы страница показывала участника и вопросы.
          if (loaded.length === 0 && (d.request_data?.results || []).length > 0) {
            // Для псевдо-деталей нужны числовые result_id/test_id: записи без
            // них пропускаем (например, архивные данные без идентификаторов).
            const fallback: ResultDetail[] = (d.request_data?.results || [])
              .filter(
                (r): r is ConclusionRequestDataResult & { result_id: number; test_id: number } =>
                  typeof r.result_id === 'number' && typeof r.test_id === 'number',
              )
              .map((r) => ({
              result_id: r.result_id,
              test_id: r.test_id,
              test_title: r.test_title || null,
              completed_at: r.completed_at || null,
              time_spent: r.time_spent ?? null,
              username: null,
              participant_name: r.participant?.name ?? null,
              participant_email: null,
              participant_device: r.participant?.device ?? null,
              participant_age: r.participant?.age ?? null,
              participant_gender: r.participant?.gender ?? null,
              answers: (r.questions || []).map((q) => ({
                question_id: q.id,
                question_order: q.order,
                question_content: q.content,
                question_time: q.time_ms ?? null,
                skip_reason: null,
                option_id: q.answer?.option_id ?? null,
                option_content: q.answer?.option_content ?? null,
                coefficient: q.answer?.coefficient ?? null,
                custom_answer: q.custom_answer ?? null,
                color_answer: q.color_answer ?? null,
              })),
            }));
            setDetails(fallback);
          } else {
            setDetails(loaded);
          }
        }
      })
      .catch((e: { response?: { data?: { detail?: string } } }) => setError(e?.response?.data?.detail || t('conclusionError')))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Остановка поллинга при размонтировании: отменяем отложенный шаг
  // и помечаем цепочку «мёртвой», чтобы await-вызовы после unmount
  // не делали setState и не планировали новые запросы.
  useEffect(() => {
    pollingActiveRef.current = true;
    return () => {
      pollingActiveRef.current = false;
      if (pollingTimerRef.current != null) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  // Счётчик прошедшего времени, пока идёт генерация (3–15 минут).
  useEffect(() => {
    if (!generating) { setGeneratingElapsed(0); return; }
    const started = data?.generation_started_at ? new Date(data.generation_started_at).getTime() : Date.now();
    const update = () => setGeneratingElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [generating, data?.generation_started_at]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      // Сохранение черновой правки (admin_draft): номер итерации не растёт,
      // PDF не пересобирается — новая версия появится после генерации.
      const updated = await updateConclusion(Number(id), editText);
      setData(updated);
      setSavedText(editText);
      setEditText(editText);
      setToastMsg(t('conclusionDraftSaved'));
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (detail) setToastMsg(detail);
      else setToastMsg(t('conclusionSaveError'));
    } finally {
      setSaving(false);
    }
  };

  // Переключение между первичными данными и текстом версии заключения.
  const switchMode = (next: 'raw' | 'saved') => {
    if (next === mode) return;
    const currentText = next === 'raw' ? rawText : (data?.response_data?.text ?? data?.response_data?.result ?? '');
    setMode(next);
    setEditText(currentText);
    setSavedText(currentText);
    setActiveVersion(activeIsCurrent || next === 'raw' ? (data?.iteration ?? 1) : activeVersion);
  };

  // Выбор версии заключения (история): показываем её текст/видимость.
  const selectVersion = (iter: number) => {
    setActiveVersion(iter);
    if (iter === (data?.iteration ?? 1)) {
      setEditText(data?.response_data?.text ?? data?.response_data?.result ?? '');
      setSavedText(editText);
    } else {
      const v = versions.find(x => x.iteration === iter);
      setEditText(v?.text ?? '');
      setSavedText(v?.text ?? '');
    }
  };

  // Видимость выбранной версии (по умолчанию новая версия скрыта).
  const toggleVersionPublic = async (iter: number, checked: boolean) => {
    if (!id) return;
    setVersionSaving(true);
    setVisibilityError('');
    try {
      const res = await patchConclusionAccess(Number(id), { is_public: checked, version: iter });
      setData(prev => {
        if (!prev) return prev;
        const next: ConclusionResponse = { ...prev, ...res, versions: res.versions ?? prev.versions };
        if (typeof res.is_public === 'boolean') next.is_public = res.is_public;
        return next;
      });
      setToastMsg(checked ? t('conclusionVersionVisible') : t('conclusionVersionHiddenDone'));
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setVisibilityError(detail || t('conclusionsErrorUpdateVisibility'));
    } finally {
      setVersionSaving(false);
    }
  };

  /** [CLINE] Генерирует TSV-таблицу первичных данных цветомера: № Стимул Метка1 Метка2 ИЦ t1 t2 t3 */
  // Палитра цветомера: 0 КрО, 1 ЛиЖ, 2 ТрЗ, 3 МаЛ, 4 ТёС, 5 ТёК, 6 АсС, 7 ЧёС
  const COLOR_HEX_FALLBACK = ['#FA6400', '#FFFF00', '#00FF00', '#FF007C', '#0000CD', '#A0522D', '#808080', '#000000'];

  const buildColorRawData = (payload?: typeof data): string => {
    const p = payload ?? data;
    const resultPayload = p?.request_data?.results?.[activeDetailIdx];
    if (!resultPayload?.questions?.length) return '';
    const colorLabels: string[] = resultPayload?.color_labels ?? [];
    const lines: string[] = [];

    // Шапка
    const header = ['№', 'Стимул', '', 'Метка 1', 'Метка 2', 'ИЦ', 't1', 't2', 't3'];
    lines.push(header.join('\t'));

    for (let i = 0; i < resultPayload.questions.length; i++) {
      const q = resultPayload.questions[i];
      const ca = q.color_answer;
      if (!ca) continue;
      const label1: string = colorLabels[ca.first] ?? COLOR_HEX_FALLBACK[ca.first] ?? `#${ca.first}`;
      const label2: string = colorLabels[ca.second] ?? COLOR_HEX_FALLBACK[ca.second] ?? `#${ca.second}`;
      const ic = String(ca.ic ?? '').replace('.', ',');
      const t1 = ca.time_first ?? 0;
      const t2 = ca.time_second ?? 0;
      const t3 = ca.time_total ?? (Number(t1) + Number(t2));
      const row = [String(i + 1), q.content, '', label1, label2, ic, String(t1), String(t2), String(t3)];
      lines.push(row.join('\t'));
    }
    return lines.join('\n');
  };

  const handleGenerate = async () => {
    if (!id) return;
    // Генерация возможна из любого режима: при «Первичные данные» текст поля —
    // это метаданные первички, при «Заключение» — текст выбранной версии.
    // Если заключение уже сгенерировано — подтверждаем создание новой версии.
    if (hasGeneratedText) {
      setConfirmState({
        message: t('conclusionRegenerateConfirm'),
        onConfirm: () => { void doGenerate(); },
      });
      return;
    }
    await doGenerate();
  };

  const doGenerate = async () => {
    if (!id) return;
    setGenerating(true);
    setGenerateError('');

    try {
      const queued = await generateConclusion(Number(id), editText);
      // Генерация асинхронная (очередь, 202): опрашиваем заключение,
      // пока воркер не завершит генерацию.
      if (queued.status === 'queued' || queued.status === 'processing') {
        await pollGeneration();
      } else {
        // Редкий случай: генерация завершилась сразу (например, is_ready).
        const current = await getConclusion(Number(id));
        setData(current);
        setMode('saved');
        const txt: string = current.response_data?.text ?? current.response_data?.result ?? '';
        setActiveVersion(current.iteration ?? 1);
        setEditText(txt);
        setSavedText(txt);
        setHasGeneratedText(!!txt);
        setGenerating(false);
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setGenerateError(detail || t('conclusionGenerateError'));
      setGenerating(false);
    }
  };

  const handleOpenCustom = async () => {
    if (!id) return;
    try {
      const items = await getCustomAnswers(Number(id));
      setCustomAnswers(items);
      const edits: typeof customEdits = {};
      for (const item of items) {
        const key = `${item.result_id}_${item.question_id}`;
        edits[key] = {
          coefficient: item.existing_coefficient != null ? String(item.existing_coefficient) : '',
          parameters: { ...(item.existing_parameters || {}) },
        };
      }
      setCustomEdits(edits);
    } catch {
      setToastMsg(t('conclusionCustomLoadError'));
    }
    setShowCustom(true);
  };

  const handleSaveCustom = async (item: CustomAnswerRow) => {
    if (!id) return;
    const key = `${item.result_id}_${item.question_id}`;
    const edit = customEdits[key] || { coefficient: '', parameters: {} };
    setCustomSaving(s => ({ ...s, [key]: true }));
    try {
      await saveCustomAnswer(Number(id), item.question_id, {
        result_id: item.result_id,
        coefficient: edit.coefficient,
        parameters: edit.parameters,
      });
      setCustomAnswers(ca => ca.map(c =>
        c.result_id === item.result_id && c.question_id === item.question_id
          ? { ...c, reviewed: true, existing_coefficient: edit.coefficient ? Number(edit.coefficient) : null, existing_parameters: edit.parameters }
          : c
      ));
    } catch {
      setToastMsg(t('conclusionCustomSaveError'));
    } finally {
      setCustomSaving(s => ({ ...s, [key]: false }));
    }
  };

  /* ── Derived data ── */
  const activeDetail = details[activeDetailIdx] ?? null;

  const makeParticipant = (d: ResultDetail | null) => d ? {
    name: d.participant_name as string | null,
    age: d.participant_age as number | null,
    gender: d.participant_gender as string | null,
    device: d.participant_device as string | null,
    testTitle: d.test_title as string | null,
    completedAt: d.completed_at as string | null,
  } : null;

  const participant = makeParticipant(activeDetail);

  const makeChartEntries = (d: ResultDetail | null): ChartEntry[] =>
    (d?.answers || [])
      .filter((a: QuestionAnswer) => a.question_time != null)
      .map((a: QuestionAnswer) => ({
        order: a.question_order,
        content: a.question_content || '',
        time: a.question_time as number,
        skipped: !!a.skip_reason,
      }));

  const chartEntries: ChartEntry[] = makeChartEntries(activeDetail);

  const TYPE_MAP: Record<string, string> = {
    individual: t('conclusionsTypeIndividual'),
    group: t('conclusionsTypeGroup'),
    comparison: t('conclusionsTypeComparison'),
  };


  if (loading) return (
    <PageContainer>
      <div style={{ color: '#888', paddingTop: 80, textAlign: 'center' }}>{t('conclusionsLoading')}</div>
    </PageContainer>
  );

  if (error) return (
    <PageContainer>
      <Header>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>{t('backBtn')}</Button>
      </Header>
      <p style={{ color: '#e53e3e' }}>{error}</p>
    </PageContainer>
  );

  return (
    <>
      {/* ── Screen layout ── */}
      <PageContainer>
        <Header>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>{t('backBtn')}</Button>
          <Title>
            {t('conclusionTitle')}
            {data?.display_code && (
              <span style={{ fontFamily: 'monospace', letterSpacing: '0.03em', marginLeft: 8 }}>
                {data.display_code}
              </span>
            )}
            {data?.updated_at ? (
              <span style={{ fontSize: '0.8rem', color: '#888', fontWeight: 400, marginLeft: 10 }}>
                {t('conclusionEditInfo').replace('{n}', String(data?.iteration ?? 1)).replace('{d}', new Date(data.updated_at).toLocaleString())}
              </span>
            ) : null}
          </Title>
          {data?.pdf_ready && (
            <Button variant="primary" size="sm" disabled={pdfDownloading} onClick={async () => {
              if (!id) return;
              setPdfDownloading(true);
              setPdfDownloadError('');
              try {
                await downloadConclusionPdf(Number(id));
              } catch (e: unknown) {
                const detail = (e as { response?: { data?: { detail?: string }; status?: number } })?.response;
                setPdfDownloadError(detail?.status === 403 ? t('conclusionPdfPaymentRequired') : detail?.data?.detail || t('conclusionPdfDownloadError'));
              } finally {
                setPdfDownloading(false);
              }
            }}>
              {pdfDownloading ? t('conclusionPdfDownloading') : t('conclusionPdfDownload')}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" style={{ color: '#e53e3e', borderColor: '#e53e3e' }} onClick={() => {
              if (!id) return;
              setConfirmState({
                message: t('conclusionDeleteConfirm'),
                onConfirm: async () => {
                  try { await deleteConclusion(Number(id)); navigate(-1); }
                  catch { setToastMsg(t('conclusionDeleteError')); }
                },
              });
            }}>{t('genDelete')}</Button>
          )}
          {pdfDownloadError && <ErrorText>{pdfDownloadError}</ErrorText>}
        </Header>

        {/* Participant info */}
        {details.length > 0 && (
          <Section>
            <SectionTitle>{t('pdfParticipant')}</SectionTitle>
            {details.length > 1 && (
              <ParticipantTabs>
                {details.map((d, i) => (
                  <ParticipantTab
                    key={i}
                    $active={i === activeDetailIdx}
                    onClick={() => setActiveDetailIdx(i)}
                  >
                    {d?.participant_name || `${t('conclusionParticipantFallback')} ${i + 1}`}
                  </ParticipantTab>
                ))}
              </ParticipantTabs>
            )}
            {participant && (
              <MetaGrid>
                {participant.testTitle && (
                  <MetaItem><MetaLabel>{t('pdfTest')}</MetaLabel><MetaValue>{participant.testTitle}</MetaValue></MetaItem>
                )}
                {participant.completedAt && (
                  <MetaItem><MetaLabel>{t('pdfDate')}</MetaLabel><MetaValue>{new Date(participant.completedAt).toLocaleString()}</MetaValue></MetaItem>
                )}
                {participant.name && (
                  <MetaItem><MetaLabel>{t('pdfParticipant')}</MetaLabel><MetaValue>{participant.name}</MetaValue></MetaItem>
                )}
                {participant.age != null && (
                  <MetaItem><MetaLabel>{t('pdfAge')}</MetaLabel><MetaValue>{participant.age}</MetaValue></MetaItem>
                )}
                {participant.gender && (
                  <MetaItem><MetaLabel>{t('pdfGender')}</MetaLabel><MetaValue>{participant.gender}</MetaValue></MetaItem>
                )}
                {participant.device && (
                  <MetaItem><MetaLabel>{t('pdfDevice')}</MetaLabel><MetaValue>{participant.device}</MetaValue></MetaItem>
                )}
                {(data?.share_code) && (
                  <MetaItem>
                    <MetaLabel>{t('pdfCode')}</MetaLabel>
                    <MetaValue style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                      {data.display_code || data.share_code}
                    </MetaValue>
                  </MetaItem>
                )}
                {(() => {
                  const others = (data?.share_codes || []).filter(c => c !== data?.share_code);
                  return others.length > 0 ? (
                    <MetaItem>
                      <MetaLabel>{t('conclusionSourceCodes')}</MetaLabel>
                      <MetaValue style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                        {others.join(', ')}
                      </MetaValue>
                    </MetaItem>
                  ) : null;
                })()}
                {(data?.missing_result_ids || []).length > 0 && (
                  <MetaItem>
                    <MetaLabel>{t('conclusionSourceMissing')}</MetaLabel>
                    <MetaValue style={{ color: '#b7791f' }}>
                      {(data?.missing_result_ids || []).join(', ')}
                    </MetaValue>
                  </MetaItem>
                )}
                <MetaItem>
                  <MetaLabel>ID</MetaLabel>
                  <MetaValue>#{data?.id} · {data?.type ? (TYPE_MAP[data.type] || data.type) : ''}</MetaValue>
                </MetaItem>
              </MetaGrid>
            )}
          </Section>
        )}

        {/* Action buttons */}
        <ActionBar>
          {activeDetail && chartEntries.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowChart(true)}>
              {t('conclusionTimeChart')}
            </Button>
          )}
          {activeDetail && (activeDetail.answers?.length ?? 0) > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowAnswers(true)}>
              {t('conclusionViewAnswers')}
            </Button>
          )}
          {isAdmin && details.some(d => d?.answers?.some((a: QuestionAnswer) => a.custom_answer)) && (
            <Button variant="outline" size="sm" onClick={handleOpenCustom}>
              {t('conclusionViewAnswers')}
              {details.some(d => d?.answers?.filter((a: QuestionAnswer) => a.custom_answer).some((a: QuestionAnswer) =>
                !customAnswers.find(c => c.question_id === a.question_id)?.reviewed
              )) && <span style={{ marginLeft: 6, color: '#f97316', fontWeight: 700 }}>!</span>}
            </Button>
          )}
        </ActionBar>

        {/* Text section */}
        <Section>
          <SectionTitle>{t('conclusionResponse')}</SectionTitle>

          {/* Переключатель: первичные данные ↔ текст заключения (для повторной отправки в LLM) */}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <ParticipantTabs style={{ margin: 0 }}>
                <ParticipantTab $active={mode === 'raw'} onClick={() => switchMode('raw')}>
                  {t('conclusionModeRaw')}
                </ParticipantTab>
                <ParticipantTab $active={mode === 'saved'} onClick={() => switchMode('saved')}>
                  {t('conclusionModeSaved')}
                </ParticipantTab>
              </ParticipantTabs>
              {mode === 'saved' && versions.length > 1 && (
                <select
                  value={activeVersion}
                  onChange={e => selectVersion(Number(e.target.value))}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: '#fff' }}
                >
                  {versions.map(v => (
                    <option key={v.iteration} value={v.iteration}>
                      {v.display_code || v.iteration} {v.is_public ? t('conclusionVersionPublic') : t('conclusionVersionHidden')}
                    </option>
                  ))}
                </select>
              )}
              {mode === 'saved' && versions.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activeIsCurrent
                      ? data?.is_public !== false
                      : (versions.find(v => v.iteration === activeVersion)?.is_public ?? false)}
                    disabled={versionSaving || generationInProgress}
                    onChange={e => toggleVersionPublic(activeVersion, e.target.checked)}
                  />
                  {t('conclusionVersionVisibleToPublic')}
                </label>
              )}
              {activeIsCurrent && (data?.iteration ?? 1) > 1 && (
                <span style={{ fontSize: '0.8rem', color: '#888' }}>
                  {t('conclusionVersionCode').replace('{code}', data?.display_code || '')}
                </span>
              )}
              {visibilityError && <ErrorText>{visibilityError}</ErrorText>}
            </div>
          )}

          <EditTextarea
            value={editText}
            onChange={e => setEditText(e.target.value)}
            placeholder={t('conclusionTextPlaceholder')}
            readOnly={!isAdmin}
          />
          {mode === 'raw' && isAdmin && (
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 6 }}>
              {t('conclusionRawHint')}
            </div>
          )}
          {generateError && <ErrorText>{generateError}</ErrorText>}
          <TextActions>
            {isAdmin && (
              <Button
                variant={isColorMode ? 'outline' : 'primary'}
                size="sm"
                onClick={handleGenerate}
                disabled={generationInProgress || !editText.trim()}
              >
                {generationInProgress
                  ? t('conclusionGenerating')
                  : t('conclusionGetBtn')}
              </Button>
            )}
            {isAdmin && isDirty && (
              <Button variant="outline" size="sm" onClick={handleSave} disabled={saving || generationInProgress}>
                {saving ? t('conclusionSaving') : t('conclusionSaveChanges')}
              </Button>
            )}
          </TextActions>
          {generationInProgress && (
            <GeneratingIndicator>
              <PulseArrow>⇄</PulseArrow>
              <span>{t('conclusionGeneratingInfo').replace('{s}', formatElapsed(generatingElapsed))}</span>
              {(data?.queue_position ?? 0) > 0 && (
                <span style={{ marginLeft: 8 }}>
                  {t('resultQueuedInfo').replace('{n}', String(data?.queue_position ?? 0))}
                </span>
              )}
            </GeneratingIndicator>
          )}
        </Section>
      </PageContainer>

      {/* ── Time chart modal ── */}
      {showChart && (
        <Overlay onClick={() => setShowChart(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('conclusionTimeChart')}</ModalTitle>
              <Button variant="outline" size="sm" onClick={() => setShowChart(false)}>{t('genCancel')}</Button>
            </ModalHeader>
            {details.length > 1 && (
              <ParticipantTabs style={{ marginBottom: 12 }}>
                {details.map((d, i) => (
                  <ParticipantTab
                    key={i}
                    $active={i === activeDetailIdx}
                    onClick={() => setActiveDetailIdx(i)}
                  >
                    {d?.participant_name || `${t('conclusionParticipantFallback')} ${i + 1}`}
                  </ParticipantTab>
                ))}
              </ParticipantTabs>
            )}
            <TimeBarChart entries={chartEntries} />
          </Modal>
        </Overlay>
      )}

      {/* ── Answers modal ── */}
      {showAnswers && activeDetail && (
        <Overlay onClick={() => setShowAnswers(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('conclusionViewAnswers')}</ModalTitle>
              <Button variant="outline" size="sm" onClick={() => setShowAnswers(false)}>{t('genCancel')}</Button>
            </ModalHeader>
            {details.length > 1 && (
              <ParticipantTabs style={{ marginBottom: 12 }}>
                {details.map((d, i) => (
                  <ParticipantTab
                    key={i}
                    $active={i === activeDetailIdx}
                    onClick={() => setActiveDetailIdx(i)}
                  >
                    {d?.participant_name || `${t('conclusionParticipantFallback')} ${i + 1}`}
                  </ParticipantTab>
                ))}
              </ParticipantTabs>
            )}
            <ATable>
              <thead>
                <tr>
                  <ATh style={{ width: '4%' }}>№</ATh>
                  <ATh style={{ width: '48%' }}>Вопрос</ATh>
                  <ATh>Ответ</ATh>
                  <ATh style={{ width: '12%' }}>мс</ATh>
                </tr>
              </thead>
              <tbody>
                {(activeDetail.answers || []).map((a: QuestionAnswer, i: number) => (
                  <tr key={a.question_id}>
                    <ATd style={{ color: '#aaa', fontSize: '0.8rem' }}>{a.question_order ?? i + 1}</ATd>
                    <ATd>{a.question_content}</ATd>
                    <ATd>
                      {a.skip_reason
                        ? <span style={{ color: '#f59e0b' }}>{a.skip_reason}</span>
                        : a.custom_answer
                          ? <span style={{ color: '#555', fontStyle: 'italic' }}>{a.custom_answer}</span>
                          : a.option_content || <span style={{ color: '#bbb' }}>—</span>
                      }
                    </ATd>
                    <ATd style={{ color: '#aaa', fontSize: '0.8rem' }}>{a.question_time ?? '—'}</ATd>
                  </tr>
                ))}
              </tbody>
            </ATable>
          </Modal>
        </Overlay>
      )}

      {/* ── Custom answers modal ── */}
      {showCustom && (
        <Overlay onClick={() => setShowCustom(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Свободные ответы</ModalTitle>
              <Button variant="outline" size="sm" onClick={() => setShowCustom(false)}>{t('genCancel')}</Button>
            </ModalHeader>
            {customAnswers.length === 0
              ? <p style={{ color: '#888' }}>Свободных ответов нет</p>
              : customAnswers.map(item => {
                  const key = `${item.result_id}_${item.question_id}`;
                  const edit = customEdits[key] || { coefficient: '', parameters: {} };
                  const saving = customSaving[key];
                  const missingCoeff = item.needs_coefficient && !edit.coefficient.trim();
                  const missingParams = item.parameter_letters.filter((l: string) => !edit.parameters[l]?.trim());
                  const hasRequiredMissing = item.in_algorithm && (missingCoeff || missingParams.length > 0);
                  return (
                    <div key={key} style={{ marginBottom: 20, padding: 16, border: `1px solid ${hasRequiredMissing ? '#f97316' : item.reviewed ? '#22c55e' : '#e2e8f0'}`, borderRadius: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
                        <span style={{ fontSize: '0.75rem', color: '#aaa' }}>#{item.question_order}</span>
                        <span style={{ fontSize: '0.9rem', color: '#333', fontWeight: 500 }}>{item.question_content}</span>
                      </div>
                      <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '8px 12px', fontSize: '0.9rem', color: '#555', fontStyle: 'italic', marginBottom: 10 }}>
                        {item.custom_text}
                      </div>
                      {item.in_algorithm && (
                        <div style={{ fontSize: '0.78rem', color: '#6366f1', marginBottom: 8 }}>
                          {t('conclusionInAlgorithm').replace('{vars}', item.variable_names.join(', '))}
                          {hasRequiredMissing && <span style={{ color: '#f97316', marginLeft: 8 }}>{t('conclusionRequiredFields')}</span>}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        {(item.needs_coefficient || !item.in_algorithm) && (
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.8rem' }}>
                            <span style={{ color: item.needs_coefficient ? '#f97316' : '#888' }}>
                              {t('conclusionCoefficient')}{item.needs_coefficient ? ' *' : ''}
                            </span>
                            <input
                              type="number"
                              step="0.1"
                              value={edit.coefficient}
                              onChange={e => setCustomEdits(ce => ({ ...ce, [key]: { ...edit, coefficient: e.target.value } }))}
                              style={{ width: 90, padding: '4px 8px', border: `1px solid ${missingCoeff ? '#f97316' : '#d1d5db'}`, borderRadius: 4, fontSize: '0.9rem' }}
                            />
                          </label>
                        )}
                        {item.parameter_letters.map((letter: string) => (
                          <label key={letter} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.8rem' }}>
                            <span style={{ color: item.in_algorithm ? '#f97316' : '#888' }}>
                              {t('conclusionParameter').replace('{letter}', letter)}{item.in_algorithm ? ' *' : ''}
                            </span>
                            <input
                              type="text"
                              value={edit.parameters[letter] || ''}
                              onChange={e => setCustomEdits(ce => ({
                                ...ce,
                                [key]: { ...edit, parameters: { ...edit.parameters, [letter]: e.target.value } }
                              }))}
                              style={{ width: 180, padding: '4px 8px', border: `1px solid ${item.in_algorithm && !edit.parameters[letter]?.trim() ? '#f97316' : '#d1d5db'}`, borderRadius: 4, fontSize: '0.9rem' }}
                            />
                          </label>
                        ))}
                        <Button variant="primary" size="sm" onClick={() => handleSaveCustom(item)} disabled={saving}>
                          {saving ? t('conclusionSaving') : item.reviewed ? t('conclusionUpdate') : t('conclusionSave')}
                        </Button>
                        {item.reviewed && <span style={{ fontSize: '0.78rem', color: '#22c55e' }}>{t('conclusionSavedMark')}</span>}
                      </div>
                    </div>
                  );
                })
            }
          </Modal>
        </Overlay>
      )}

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}

      {/* ── Confirm modal for delete ── */}
      {confirmState && (
        <Overlay onClick={() => setConfirmState(null)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{confirmState.message}</p>
            <ConfirmActions>
              <Button variant="outline" size="sm" onClick={() => setConfirmState(null)}>
                {t('genCancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  confirmState.onConfirm();
                  setConfirmState(null);
                }}
              >
                {t('conclusionsConfirmAction')}
              </Button>
            </ConfirmActions>
          </ConfirmModal>
        </Overlay>
      )}
    </>
  );
};

export default ConclusionPage;
